import { Component, DestroyRef, inject, Input, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { OnboardingService } from '../onboarding.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ArivoLicensePlateInputComponent, ArivoStepperComponent } from '@shared/components/ui-elements';
import { LicensePlate } from '@shared/models';
import { requireLicensePlate, showSpinner } from '@shared/utils';
import { pairwise, take } from 'rxjs';
import { ToastService } from '@shared/services';
import { TranslateService } from '@ngx-translate/core';
import { AmplitudeEventService } from '@core/services';
import { RegistrationLicensePlateSubmitted, RegistrationStarted } from '@integrations/amplitude';

@Component({
  selector: 'app-onboarding-first-step',
  templateUrl: './onboarding-first-step.component.html',
  styleUrl: './onboarding-first-step.component.scss',
})
export class OnboardingFirstStepComponent implements OnInit {
  private destroyRef = inject(DestroyRef);
  @ViewChild(ArivoLicensePlateInputComponent) licensePlateInputComponent!: ArivoLicensePlateInputComponent;

  @Input() stepperComponent?: ArivoStepperComponent;

  constructor(
    private _formBuilder: FormBuilder,
    private onboardingService: OnboardingService,
    private toastService: ToastService,
    private translateService: TranslateService,
    private amplitudeEventService: AmplitudeEventService,
  ) {
    this.licensePlateForm
      .get('licensePlate')
      ?.valueChanges.pipe(pairwise(), takeUntilDestroyed(this.destroyRef))
      .subscribe(([oldPlate, newPlate]: [LicensePlate | null, LicensePlate | null]) => {
        if (oldPlate?.license_plate !== newPlate?.license_plate || oldPlate?.license_plate_country !== newPlate?.license_plate_country) {
          if (newPlate) {
            this.licensePlateForm.get('confirmRights')?.setValue(false);
          }
        }
      });
  }

  ngOnInit(): void {
    this.amplitudeEventService.trackEvent(
      new RegistrationStarted({
        registrationFlowType: 'ppu',
      }),
    );
  }

  get publicProductsPageEnabled(): boolean {
    return this.onboardingService.publicProductsPageEnabled;
  }

  licensePlateForm = this._formBuilder.group({
    licensePlate: [<LicensePlate>{}, requireLicensePlate],
    confirmRights: [false, Validators.requiredTrue],
  });

  enterCheck(event: KeyboardEvent) {
    if (event.key === 'Enter') {
      this.next();
    }
  }

  next(): void {
    this.licensePlateForm.markAllAsTouched();
    this.licensePlateInputComponent.markAllAsTouched();
    if (this.licensePlateForm.valid) {
      const licensePlate = this.licensePlateForm.value.licensePlate;
      this.onboardingService
        .getOnboardingSessionStatus(licensePlate as LicensePlate)
        .pipe(showSpinner(), take(1))
        .subscribe({
          next: () => {
            this.amplitudeEventService.trackEvent(
              new RegistrationLicensePlateSubmitted({
                registrationFlowType: 'ppu',
              }),
            );
            this.stepperComponent?.next();
          },
          error: (error) => {
            if (error.status === 429) {
              this.toastService.add({
                message: error.error.message ?? this.translateService.instant('Common.Errors.UnknownError'),
                type: 'error',
              });
            } else {
              this.toastService.add({
                message: this.translateService.instant('LogIn.CouldNotSendEmail'),
                type: 'error',
              });
            }
          },
        });
    }
  }
}
